import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * ABN (Australian Business Number) Validator
 *
 * Validates that ABN is exactly 11 numeric digits.
 */

/**
 * Angular ValidatorFn for ABN validation
 * Use this in reactive forms for ABN fields
 *
 * @example
 * ```typescript
 * this.form = this.fb.group({
 *   abnNumber: ['', [abnValidator()]]
 * });
 * ```
 */
export function abnValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    // Empty values should be handled by required validator
    if (!value || value === '') {
      return null;
    }

    const cleaned = String(value).replace(/\D/g, '');

    // Check if it's a partial input (user is still typing)
    if (cleaned.length > 0 && cleaned.length < 11) {
      return { abnIncomplete: { requiredLength: 11, actualLength: cleaned.length } };
    }

    // If not exactly 11 digits, it's invalid
    if (cleaned.length !== 11) {
      return { abnInvalid: true };
    }

    return null;
  };
}

/**
 * Custom validator function for use in dynamic forms
 * Returns the same structure as Angular validators but works with raw values
 */
export function abnCustomValidator(value: string | null): ValidationErrors | null {
  if (!value || value === '') {
    return null;
  }

  const cleaned = String(value).replace(/\D/g, '');

  if (cleaned.length > 0 && cleaned.length < 11) {
    return { abnIncomplete: { requiredLength: 11, actualLength: cleaned.length } };
  }

  if (cleaned.length !== 11) {
    return { abnInvalid: true };
  }

  return null;
}

/**
 * Formats an ABN string as XX XXX XXX XXX
 * @param value - The raw ABN value
 * @returns Formatted ABN string
 */
export function formatAbn(value: string | null | undefined): string {
  if (!value) return '';

  const cleaned = String(value).replace(/\D/g, '');

  if (cleaned.length === 0) return '';

  if (cleaned.length <= 2) {
    return cleaned;
  } else if (cleaned.length <= 5) {
    return `${cleaned.slice(0, 2)} ${cleaned.slice(2)}`;
  } else if (cleaned.length <= 8) {
    return `${cleaned.slice(0, 2)} ${cleaned.slice(2, 5)} ${cleaned.slice(5)}`;
  } else {
    return `${cleaned.slice(0, 2)} ${cleaned.slice(2, 5)} ${cleaned.slice(5, 8)} ${cleaned.slice(8, 11)}`;
  }
}

/**
 * Strips all non-digit characters from an ABN
 * @param value - The ABN value (possibly formatted)
 * @returns Clean numeric string
 */
export function cleanAbn(value: string | null | undefined): string {
  if (!value) return '';
  return String(value).replace(/\D/g, '');
}
